// Rateio de compra repartida em varias notas ([DA-679]).
//
// A tela nao calcula nada: ela pede a previa ao backend a cada troca de papel e mostra o que voltou.
// E de proposito — a gravacao passa pela MESMA conta, entao nao ha como a tela prometer um numero e
// o banco receber outro. O unico dado que sai daqui e QUAIS produtos gravar, nunca QUANTO.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, AlertTriangle } from 'lucide-react';
import { api } from '../../lib/api';
import { useToast } from '../../components/Toast';
import { useDialog } from '../../components/DialogProvider';
import { formatMoeda, formatDataSP } from '@fiodecobre/shared';

type PapelNota = 'paga' | 'bonificacao';
type SituacaoLinha = 'rateado' | 'so_bonificacao';

interface NotaGrupo {
  id: number;
  numero: string;
  serie: string | null;
  data_emissao: string | null;
  natureza_operacao: string | null;
  valor_produtos: number;
  valor_total: number;
  outras_despesas: number;
  papel: PapelNota;
}

interface LinhaRateio {
  produto_id: number;
  descricao: string;
  codigo_interno: string | null;
  codigo_fornecedor: string | null;
  un_pagas: number;
  un_bonificadas: number;
  un_total: number;
  custo_atual: number;
  custo_novo: number | null;
  // A API manda, a tela NAO mostra (decisao dele, 05/09/2026 — a coluna Diferenca saiu). Depois de
  // gravar ela virava `0.0%` no amarelo da casa, gastando atencao pra anunciar um nao-evento ao
  // lado do `✓ Atualizado`, e os dois numeros de custo ja dizem a mudanca. Segue no payload porque
  // e' o que o teste do servico usa pra guardar a conta.
  variacao_pct: number | null;
  situacao: SituacaoLinha;
  ja_aplicado: boolean;
  custo_conferido: number | null;
  divergencia: number | null;
}

interface Previa {
  fornecedor: string;
  notas: NotaGrupo[];
  tolerancia_divergencia: number;
  resumo: {
    mercadoria_paga: number;
    bonificacao_recebida: number;
    custos_compartilhados: number;
    impostos: number;
    desembolso: number;
    lista_total: number;
    fator_compra: number;
    abatimento_pct: number;
    soma_reconstruida: number;
    diferenca_arredondamento: number;
  };
  linhas: LinhaRateio[];
}

const AMARELO = '#fde68a';

function fmtUn(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toLocaleString('pt-BR', { maximumFractionDigits: 3 });
}
function fmtCusto(n: number): string {
  return n.toLocaleString('pt-BR', { minimumFractionDigits: 4, maximumFractionDigits: 4 });
}

export function NotaEntradaRatear() {
  const navigate = useNavigate();
  const toast = useToast();
  const dialog = useDialog();
  const [searchParams] = useSearchParams();

  const ids = useMemo(() => {
    return (searchParams.get('ids') ?? '')
      .split(',')
      .map((s) => Number(s.trim()))
      .filter((n) => Number.isInteger(n) && n > 0);
  }, [searchParams]);

  const [previa, setPrevia] = useState<Previa | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  // Qual linha esta gravando agora (produto_id), pra travar so o botao dela.
  const [gravando, setGravando] = useState<number | null>(null);
  // `null` = ainda nao mexi neste papel, entao vale a sugestao do backend (CFOP/natureza).
  const [papeis, setPapeis] = useState<Record<number, PapelNota>>({});

  const buscar = useCallback(async () => {
    setCarregando(true);
    setErro('');
    try {
      const corpo = {
        notas: ids.map((id) => ({ id, papel: papeis[id] ?? null })),
      };
      const dados = await api.post<Previa>('/api/notas-entrada/ratear/previa', corpo);
      setPrevia(dados);
    } catch (e: any) {
      setErro(e?.message || 'Nao consegui montar o rateio.');
      setPrevia(null);
    } finally {
      setCarregando(false);
    }
  }, [ids, papeis]);

  useEffect(() => { void buscar(); }, [buscar]);

  function trocarPapel(id: number, papel: PapelNota) {
    setPapeis((p) => ({ ...p, [id]: papel }));
  }

  // Uma linha por vez. A confirmacao nomeia o produto e os DOIS valores: as linhas tem ~50px e o
  // clique errado grava custo no produto vizinho — o botao e o gesto dele, a previa e minha.
  async function atualizarCusto(l: LinhaRateio) {
    if (!previa || l.custo_novo == null) return;
    const ok = await dialog.confirmar(
      `${l.descricao}\n\nCusto de ${fmtCusto(l.custo_atual)} para ${fmtCusto(l.custo_novo)}. `
      + 'O valor anterior fica no historico do produto, com as notas desta compra. '
      + 'Estoque e preco de venda nao mudam.',
      { titulo: 'Atualizar o custo', textoConfirmar: 'Atualizar' },
    );
    if (!ok) return;
    setGravando(l.produto_id);
    try {
      await api.post('/api/notas-entrada/ratear/aplicar', {
        notas: previa.notas.map((n) => ({ id: n.id, papel: n.papel })),
        produto_ids: [l.produto_id],
      });
      toast.sucesso(`Custo de ${l.codigo_interno || l.produto_id} atualizado.`);
      // Recarrega do servidor em vez de remendar a linha na tela: o que vale e o que ficou no
      // banco, e e assim que a linha passa a se reconhecer como ja aplicada.
      await buscar();
    } catch (e: any) {
      toast.erro(e?.message || 'Nao consegui gravar.');
    } finally {
      setGravando(null);
    }
  }

  const diferenca = previa?.resumo.diferenca_arredondamento ?? 0;

  return (
    <div>
      <div className="pagina-header">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/notas-entrada')}
            className="p-1.5 rounded hover:bg-black/5"
            title="Voltar para as Notas de Entrada"
          >
            <ArrowLeft size={18} />
          </button>
          <div>
            <h2 className="pagina-titulo">Ratear compra</h2>
            {previa && (
              <div className="text-xs muted">
                {previa.fornecedor} · {previa.notas.length} notas
                {previa.notas[0]?.data_emissao ? ` · emitidas em ${formatDataSP(previa.notas[0].data_emissao)}` : ''}
              </div>
            )}
          </div>
        </div>
      </div>

      {erro && (
        <div className="rounded-lg border p-4 my-3 text-sm" style={{ borderColor: '#b45309', backgroundColor: AMARELO, color: '#000' }}>
          {erro}
        </div>
      )}

      {carregando && !previa && <div className="p-8 text-center muted">Montando o rateio...</div>}

      {previa && (
        <>
          {/* ---------- 1. AS NOTAS ---------- */}
          <div className="rounded-lg border mb-3" style={{ borderColor: 'var(--input-border)', backgroundColor: 'var(--card-bg)' }}>
            <div className="px-3 py-2 text-xs font-bold border-b flex items-center gap-2" style={{ borderColor: 'var(--input-border)' }}>
              AS NOTAS DESTA COMPRA
              <span className="font-normal muted">o sistema sugere pelo CFOP — confira, porque o CFOP nao e garantia</span>
            </div>
            {previa.notas.map((n) => (
              <div key={n.id} className="flex items-center gap-3 px-3 py-2 border-b text-sm" style={{ borderColor: 'var(--input-border)' }}>
                <div style={{ width: 120 }}>
                  <div className="font-bold">{n.numero}</div>
                  <div className="text-xs muted">Serie {n.serie ?? '0'}</div>
                </div>
                <div className="flex-1 text-xs muted truncate">{n.natureza_operacao}</div>
                <div style={{ width: 130 }} className="text-right font-semibold">{formatMoeda(n.valor_total)}</div>
                <div className="inline-flex rounded-lg overflow-hidden border" style={{ borderColor: 'var(--input-border)' }}>
                  {(['paga', 'bonificacao'] as PapelNota[]).map((p) => {
                    const ativo = n.papel === p;
                    return (
                      <button
                        key={p}
                        onClick={() => trocarPapel(n.id, p)}
                        disabled={carregando}
                        className="px-3 py-1.5 text-xs font-semibold disabled:opacity-60"
                        style={ativo
                          ? (p === 'bonificacao'
                              ? { backgroundColor: AMARELO, color: '#000' }
                              : { backgroundColor: '#11224a', color: '#fff' })
                          : { backgroundColor: 'transparent', color: 'var(--text-muted)' }}
                      >
                        {p === 'paga' ? 'Paga' : 'Bonificacao'}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {/* ---------- 2. DESEMBOLSO ---------- */}
          <div className="rounded-lg border mb-3 overflow-hidden" style={{ borderColor: 'var(--input-border)', backgroundColor: 'var(--card-bg)' }}>
            <div className="px-3 py-2 text-xs font-bold border-b" style={{ borderColor: 'var(--input-border)' }}>
              O QUE A COMPRA CUSTOU DE VERDADE
            </div>
            <div className="grid" style={{ gridTemplateColumns: 'repeat(4, minmax(0,1fr))' }}>
              <Conta rotulo="Mercadoria paga" valor={previa.resumo.mercadoria_paga} obs="o que saiu do caixa" />
              <Conta rotulo="Bonificacao recebida" valor={previa.resumo.bonificacao_recebida}
                     obs="mercadoria gratis — nao entra no que voce pagou" />
              <Conta rotulo="Impostos e despesas" valor={previa.resumo.impostos + previa.resumo.custos_compartilhados}
                     obs="DIFAL/ST, IPI e frete — inclusive os da bonificacao" />
              {/* A frase que ele confere sem depender de mim: a compra saiu N% abaixo da tabela, e
                  e esse abatimento que se reparte por TODOS os produtos ([DA-679], regra dele). */}
              <Conta rotulo="Desembolso real" valor={previa.resumo.desembolso}
                     obs={`${Math.abs(previa.resumo.abatimento_pct).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}% abaixo da tabela de ${formatMoeda(previa.resumo.lista_total)}`}
                     destaque />
            </div>
          </div>

          {/* ---------- 3. PRODUTOS ---------- */}
          <div className="rounded-lg border" style={{ borderColor: 'var(--input-border)', backgroundColor: 'var(--card-bg)' }}>
            <div className="px-3 py-2 text-xs font-bold border-b flex items-center gap-2" style={{ borderColor: 'var(--input-border)' }}>
              CUSTO POR PRODUTO
              <span className="font-normal muted">cada linha grava a sua — nada muda ate voce clicar em Atualizar Custo</span>
            </div>
            <table className="w-full text-sm">
              <thead className="grid-header">
                <tr>
                  <th className="text-left px-2 py-1.5" style={{ width: 78 }}>Codigo</th>
                  <th className="text-left px-2 py-1.5">Produto</th>
                  <th className="text-right px-2 py-1.5" style={{ width: 88 }}>Un. pagas</th>
                  <th className="text-right px-2 py-1.5" style={{ width: 92 }}>Un. gratis</th>
                  <th className="text-right px-2 py-1.5" style={{ width: 84 }}>Un. total</th>
                  <th className="text-right px-2 py-1.5" style={{ width: 106 }}>Custo hoje</th>
                  <th className="text-right px-2 py-1.5" style={{ width: 112 }}>Custo real</th>
                  <th className="text-center px-2 py-1.5" style={{ width: 150 }}></th>
                </tr>
              </thead>
              <tbody className="grid-body">
                {previa.linhas.map((l) => {
                  const muda = l.custo_novo != null;
                  const divergente = l.divergencia != null
                    && Math.abs(l.divergencia) > previa.tolerancia_divergencia;
                  return (
                    // ⛔ Sem `opacity` pra apagar linha nenhuma ([DA-678]): o texto `muted`
                    // do tema ja mede 3,93:1, e a opacidade por cima levava pra perto de 2 — medido
                    // na tela viva. O que separa as linhas e o traco no Custo real, a ausencia do
                    // botao e o rotulo, que nao custam legibilidade.
                    <tr key={l.produto_id} className="border-b" style={{ borderColor: 'var(--input-border)' }}>
                      {/* Codigo do NOSSO cadastro. Mesma classe da tela irma (RevisarMargens), e o
                          `codigo-curto` e' o que impede o iOS de virar o numero em link de telefone. */}
                      <td className="px-2 py-1.5 codigo-curto text-xs">{l.codigo_interno || '—'}</td>
                      <td className="px-2 py-1.5">
                        <div className="font-semibold">{l.descricao}</div>
                        <div className="text-xs muted">
                          {l.codigo_fornecedor ? `cod. forn. ${l.codigo_fornecedor}` : `produto ${l.produto_id}`}
                          {l.un_bonificadas > 0
                            && ` · pagou ${fmtUn(l.un_pagas)}, ganhou ${fmtUn(l.un_bonificadas)}`}
                        </div>
                        {divergente && (
                          <div className="text-xs mt-0.5 inline-flex items-center gap-1 px-1.5 rounded"
                               style={{ backgroundColor: AMARELO, color: '#000', fontWeight: 600 }}>
                            <AlertTriangle size={12} />
                            o custo de hoje nao veio desta compra — estas notas dao {fmtCusto(l.custo_conferido ?? 0)}
                          </div>
                        )}
                      </td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{fmtUn(l.un_pagas)}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">
                        {l.un_bonificadas > 0
                          ? <span className="inline-block px-1.5 rounded font-bold" style={{ backgroundColor: AMARELO, color: '#000' }}>{fmtUn(l.un_bonificadas)}</span>
                          : <span className="muted">—</span>}
                      </td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{fmtUn(l.un_total)}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{fmtCusto(l.custo_atual)}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums font-bold">
                        {l.custo_novo != null ? fmtCusto(l.custo_novo) : <span className="muted font-normal">—</span>}
                      </td>
                      {/* A acao mora na LINHA: cada produto e uma decisao dele. So fica sem botao a
                          linha que chegou SO na bonificacao, porque essa mantem o custo por decisao
                          dele — nao e permissao negada, e ausencia de alvo. */}
                      <td className="px-2 py-1.5 text-center">
                        {l.ja_aplicado ? (
                          <span className="text-xs font-bold px-2 py-1 rounded inline-block"
                                style={{ backgroundColor: AMARELO, color: '#000' }}>
                            ✓ Atualizado
                          </span>
                        ) : muda ? (
                          <button
                            onClick={() => void atualizarCusto(l)}
                            disabled={gravando != null}
                            className="text-xs font-semibold px-2.5 py-1.5 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                            style={{ backgroundColor: '#11224a', color: '#fff' }}
                            title={`Gravar ${fmtCusto(l.custo_novo!)} no custo de ${l.descricao}`}
                          >
                            {gravando === l.produto_id ? 'Gravando...' : 'Atualizar Custo'}
                          </button>
                        ) : (
                          // Sem botao porque nao ha custo novo a gravar. O motivo mora AQUI, no
                          // lugar do botao que falta — antes ele vivia na coluna Diferenca, que saiu.
                          <span className="text-xs muted italic">chegou sem compra</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* ---------- rodape: a prova de fechamento ---------- */}
            <div className="flex items-center gap-4 px-3 py-2.5 border-t flex-wrap" style={{ borderColor: 'var(--input-border)' }}>
              <div className="text-xs" style={{ lineHeight: 1.5 }}>
                <span className="inline-block px-1.5 rounded font-bold mr-1" style={{ backgroundColor: AMARELO, color: '#000' }}>
                  {Math.abs(diferenca) <= 0.05 ? 'FECHA' : 'CONFIRA'}
                </span>
                soma de (custo real × unidades) = <b className="tabular-nums">{formatMoeda(previa.resumo.soma_reconstruida)}</b>
                {' '}contra o desembolso de <b className="tabular-nums">{formatMoeda(previa.resumo.desembolso)}</b>
                <br />
                diferenca de <b className="tabular-nums">{formatMoeda(Math.abs(diferenca))}</b>
                {' '}— arredondamento de centavo distribuido em {previa.linhas.length} linhas
              </div>
              {/* Nao ha mais botao de gravar aqui: cada linha grava a sua. O rodape ficou so com a
                  prova de fechamento e a saida. */}
              <div className="ml-auto">
                <button onClick={() => navigate('/notas-entrada')} className="btn-secundario px-4 py-2 text-sm">
                  Voltar para as notas
                </button>
              </div>
            </div>
          </div>

          <div className="text-xs muted mt-2 mb-6">
            Grava so o <b>Custo</b> do cadastro, com registro no historico do produto nomeando as notas.
            Nao mexe em estoque, preco de venda nem CFOP. Produto que chegou <b>so</b> na nota de
            bonificacao mantem o custo que ja tinha.
          </div>
        </>
      )}
    </div>
  );
}

function Conta({ rotulo, valor, obs, destaque }: { rotulo: string; valor: number; obs: string; destaque?: boolean }) {
  return (
    <div
      className="px-3 py-2 border-l first:border-l-0"
      style={destaque
        ? { borderColor: 'var(--input-border)', backgroundColor: '#11224a' }
        : { borderColor: 'var(--input-border)' }}
    >
      <div className="text-[10px] uppercase tracking-wide" style={{ color: destaque ? '#9fb6e4' : 'var(--text-muted)' }}>
        {rotulo}
      </div>
      <div className="text-lg font-bold tabular-nums" style={destaque ? { color: '#fff' } : undefined}>
        {formatMoeda(valor)}
      </div>
      <div className="text-[10.5px]" style={{ color: destaque ? '#9fb6e4' : 'var(--text-muted)' }}>{obs}</div>
    </div>
  );
}
